import { computed } from 'vue';
import { useRequestContentStore } from '@/store/useRequestContentStore';
import { useCollectionsStore } from '@/store/useCollectionsStore';

export const useRequestContents = () => {
  const store = useRequestContentStore();
  const collectionsStore = useCollectionsStore();

  // Load collections only if not already loaded
  const ensureCollectionsLoaded = async () => {
    // Check if collections are already loaded and not empty
    // This prevents unnecessary reloading and flickering
    if (collectionsStore.list.length === 0) {
      await collectionsStore.load();
    }
  };

  // Display pages for pagination - this adds business logic not in store
  const displayPages = computed(() => {
    const pages: number[] = [];
    const current = store.meta.currentPage;
    const total = store.meta.totalPages;

    // Simple pagination logic - show up to 5 pages around current
    const start = Math.max(1, current - 2);
    const end = Math.min(total, current + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  });

  // Actions - only keep actions that combine multiple stores
  const loadRequestContentsWithCollections = async (page?: number, perPage?: number) => {
    // Update store pagination parameters if provided
    if (page !== undefined) {
      store.meta.currentPage = page;
    }
    if (perPage !== undefined) {
      store.meta.perPage = perPage;
    }

    // Load collections and request contents in parallel
    // Collections are loaded first or in parallel to prevent flickering
    await Promise.all([ensureCollectionsLoaded(), store.loadRequestContents()]);
  };

  // Pagination actions
  const changePage = async (page: number) => {
    // Load collections first (if needed) to ensure they're available when request contents render
    await ensureCollectionsLoaded();
    await store.changePage(page);
  };

  const changePerPage = async (perPage: number) => {
    if (perPage < 1 || perPage > 100) return;

    store.meta.perPage = perPage;
    store.meta.currentPage = 1; // Reset to first page
    await loadRequestContentsWithCollections();
  };

  return {
    // State - direct access to store data, no enrichment
    requestContents: computed(() => store.requestContents),
    displayPages,
    // Pagination info directly from store
    meta: computed(() => store.meta),
    isLoading: computed(() => store.isLoading),
    hasMultiplePages: computed(() => store.hasMultiplePages),
    isFirstPage: computed(() => store.isFirstPage),
    isLastPage: computed(() => store.isLastPage),
    // Actions
    loadRequestContentsWithCollections,
    changePage,
    changePerPage,
    // URL validation methods
    checkCustomUrlAvailability: store.checkCustomUrlAvailability,
    updateRequestContent: store.updateRequestContent,
  };
};
